import { useState, useEffect } from 'react';

// The browser fires this event before showing its own A2HS banner.
// It is not in the standard TS lib, so we declare it here.
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'pwa_install_dismissed_at';
const DISMISS_TTL_MS = 3 * 24 * 60 * 60 * 1000; // re-show after 3 days

function wasDismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISSED_KEY);
  if (!raw) return false;
  return Date.now() - Number(raw) < DISMISS_TTL_MS;
}

export interface InstallPromptState {
  /** True when the browser has a deferred install event ready (Android/Desktop Chrome). */
  canNativeInstall: boolean;
  /** True when the prompt should be shown to the user. */
  shouldShowPrompt: boolean;
  /** Trigger the native browser install dialog. No-op on iOS. */
  triggerInstall(): Promise<void>;
  /** Dismiss the prompt and suppress it for 3 days. */
  dismiss(): void;
}

/**
 * Manages the PWA install prompt lifecycle.
 *
 * On Android/Chrome: intercepts `beforeinstallprompt` and exposes a
 * `triggerInstall()` that opens the native dialog.
 * On iOS: `canNativeInstall` stays false — the UI falls back to
 * step-by-step Safari instructions.
 */
export function useInstallPrompt(): InstallPromptState {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(wasDismissedRecently);

  useEffect(() => {
    function handleBeforeInstallPrompt(e: Event) {
      // Prevent the browser's default mini-infobar from appearing.
      e.preventDefault();
      setDeferredEvent(e as BeforeInstallPromptEvent);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  // Hide the prompt once the user installs the app mid-session.
  useEffect(() => {
    function handleAppInstalled() {
      setDeferredEvent(null);
    }
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  async function triggerInstall() {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    if (outcome === 'accepted') {
      setDeferredEvent(null);
    }
  }

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setDismissed(true);
  }

  return {
    canNativeInstall: deferredEvent !== null,
    shouldShowPrompt: !dismissed,
    triggerInstall,
    dismiss,
  };
}
